import React, { useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import AceEditor from 'react-ace'
import 'ace-builds/src-noconflict/mode-javascript'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/theme-xcode'
import { validateSource } from '../../SourceEditor/utils/smartEndpointValidation'
import { TEST_MODES } from './testFixtures'

function TestConfig ({ test, selectedIndex, onConfigChange, classes }) {
  const assertionsEditorRef = useRef(null)

  const handleAssertionsChange = useCallback((value) => {
    onConfigChange(selectedIndex, 'assertions', value)
    if (assertionsEditorRef.current) {
      assertionsEditorRef.current.getSession().setAnnotations(validateSource(value))
    }
  }, [selectedIndex, onConfigChange])

  return (
    <div className={classes.configPanel}>
      <div className={classes.fieldRow}>
        <div className={classes.fieldGroup} style={{ flex: 1 }}>
          <label className={classes.fieldLabel}>Name</label>
          <input
            className={classes.nameInput}
            value={test.name}
            onChange={(e) => onConfigChange(selectedIndex, 'name', e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label className={classes.fieldLabel}>Mode</label>
          <div className={classes.modeSelector} style={{ height: '30px' }}>
            {TEST_MODES.map(mode => (
              <button
                key={mode.id}
                className={classes.modeOption}
                style={test.testMode === mode.id
                  ? { backgroundColor: mode.color, color: 'white', height: '100%' }
                  : { height: '100%' }}
                onClick={() => onConfigChange(selectedIndex, 'testMode', mode.id)}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {(test.modeOverride || test.policyOverride != null) && (
        <div style={{ display: 'flex', gap: 'var(--gap-sm)', flexWrap: 'wrap' }}>
          {test.modeOverride && (
            <span style={{ padding: '2px 8px', fontSize: '11px', fontWeight: 600, borderRadius: 'var(--border-radius-sm)', backgroundColor: test.modeOverride === 'query' ? 'var(--color-primary-050)' : 'var(--color-danger-050)', color: test.modeOverride === 'query' ? 'var(--color-primary-700)' : 'var(--color-danger-700)', border: '1px solid ' + (test.modeOverride === 'query' ? 'var(--color-primary-200)' : 'var(--color-danger-200)') }}>
              Mode: {test.modeOverride === 'query' ? 'Read Only' : 'Read/Write'}
            </span>
          )}
          {test.policyOverride != null && (
            <span style={{ padding: '2px 8px', fontSize: '11px', fontWeight: 600, borderRadius: 'var(--border-radius-sm)', backgroundColor: 'var(--color-warning-050)', color: 'var(--color-warning-700)', border: '1px solid var(--color-warning-200)' }}>
              Policy override ({test.policyOverride.length} {test.policyOverride.length === 1 ? 'entry' : 'entries'})
            </span>
          )}
        </div>
      )}

      <div className={classes.fieldGroup}>
        <label className={classes.fieldLabel}>Input</label>
        <div className={classes.aceWrapper}>
          <AceEditor
            key={`input-${selectedIndex}`}
            height='80px'
            width='100%'
            mode='json'
            theme='xcode'
            name={`test-input-${selectedIndex}`}
            value={test.input}
            onChange={(val) => onConfigChange(selectedIndex, 'input', val)}
            tabSize={2}
            fontSize={12}
            showPrintMargin={false}
            editorProps={{ $blockScrolling: true }}
            setOptions={{ useWorker: false, showLineNumbers: false }}
          />
        </div>
      </div>

      <div className={classes.fieldGroup}>
        <label className={classes.fieldLabel}>Mock Responses</label>
        <div className={classes.aceWrapper}>
          <AceEditor
            key={`responses-${selectedIndex}`}
            height='80px'
            width='100%'
            mode='json'
            theme='xcode'
            name={`test-responses-${selectedIndex}`}
            value={test.responses}
            onChange={(val) => onConfigChange(selectedIndex, 'responses', val)}
            tabSize={2}
            fontSize={12}
            showPrintMargin={false}
            editorProps={{ $blockScrolling: true }}
            setOptions={{ useWorker: false, showLineNumbers: false }}
          />
        </div>
      </div>

      <div className={classes.fieldGroup}>
        <label className={classes.fieldLabel}>Assertions</label>
        <div className={classes.aceWrapper}>
          <AceEditor
            key={`assertions-${selectedIndex}`}
            height='80px'
            width='100%'
            mode='javascript'
            theme='xcode'
            name={`test-assertions-${selectedIndex}`}
            value={test.assertions}
            tabSize={2}
            fontSize={12}
            showPrintMargin={false}
            editorProps={{ $blockScrolling: true }}
            setOptions={{ useWorker: false, showLineNumbers: false }}
            onLoad={(editor) => {
              assertionsEditorRef.current = editor
              editor.getSession().setAnnotations(validateSource(test.assertions))
            }}
            onChange={handleAssertionsChange}
          />
        </div>
      </div>
    </div>
  )
}

TestConfig.propTypes = {
  test: PropTypes.object.isRequired,
  selectedIndex: PropTypes.number.isRequired,
  onConfigChange: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
}

export default TestConfig
